import { Button, Tooltip } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { planState, type AuditDto } from "@audit-plan/shared";
import { palette } from "../../theme";
import { useIsMobile } from "../useIsMobile";

export const planColor = (a: Pick<AuditDto, "status" | "outcome">) => {
  if (a.outcome === "phaseout") return palette.crit;
  const s = planState(a);
  return s === "planned" ? palette.secX : s === "exec" ? palette.warn : palette.ok;
};

export function PlanLegend() {
  const { t } = useTranslation();
  const items: [string, string][] = [
    [t("ps_planned"), palette.secX],
    [t("ps_exec"), palette.warn],
    [t("ps_done"), palette.ok],
    [t("phaseout"), palette.crit],
  ];
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 16px", fontSize: 12.5, color: palette.muted, marginBottom: 10 }}>
      {items.map(([l, c]) => (
        <span key={l} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <i style={{ width: 10, height: 10, borderRadius: 3, background: c, display: "inline-block" }} />
          {l}
        </span>
      ))}
    </div>
  );
}

interface Props {
  year: number;
  audits: AuditDto[];
  canCreate: boolean;
  initials: (id: string) => string;
  onOpen: (a: AuditDto) => void;
  onAdd: (date: string) => void;
}

export function PlanCalendar({ year, audits, canCreate, initials, onOpen, onAdd }: Props) {
  const { t, i18n } = useTranslation();
  const mobile = useIsMobile();
  const loc = i18n.language === "en" ? "en-GB" : "it-IT";
  const nowM = dayjs().year() === year ? dayjs().month() : -1;
  return (
    <div style={{ display: "grid", gridTemplateColumns: mobile ? "minmax(0, 1fr)" : "repeat(auto-fill, minmax(260px, 1fr))", gap: mobile ? 8 : 12 }}>
      {Array.from({ length: 12 }, (_, i) => {
        const mm = String(i + 1).padStart(2, "0");
        const items = audits.filter((a) => a.plannedDate && a.plannedDate.slice(5, 7) === mm).sort((x, y) => (x.plannedDate! < y.plannedDate! ? -1 : 1));
        return (
          <div key={mm} style={{ background: palette.card, border: `1px solid ${i === nowM ? palette.primary : palette.hair}`, borderRadius: 10, display: "flex", flexDirection: "column", minHeight: mobile ? 0 : 120, minWidth: 0 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 10px", fontWeight: 700, background: i === nowM ? palette.primarySoft : undefined, borderRadius: mobile && !items.length ? 10 : "10px 10px 0 0", borderBottom: mobile && !items.length ? 0 : `1px solid ${palette.hair}` }}>
              <span style={{ textTransform: "capitalize" }}>{new Date(2000, i, 1).toLocaleDateString(loc, { month: "long" })}</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 12, color: palette.muted, fontWeight: 400 }}>{items.length || ""}</span>
                {canCreate && (
                  <Tooltip title={t("newAudit")}>
                    <Button size="small" type="text" icon={<PlusOutlined />} onClick={() => onAdd(`${year}-${mm}-01`)} />
                  </Tooltip>
                )}
              </span>
            </div>
            {mobile && !items.length ? null : (
            <div style={{ padding: 8, display: "grid", gap: 6, alignContent: "start" }}>
              {items.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => onOpen(a)}
                  style={{ textAlign: "left", font: "inherit", cursor: "pointer", background: palette.sunk, border: 0, borderLeft: `4px solid ${planColor(a)}`, borderRadius: 6, padding: "8px 10px", minWidth: 0, overflowWrap: "anywhere" }}
                >
                  <b>{a.plannedDate!.slice(8, 10)}{a.plannedDateTo ? `–${a.plannedDateTo.slice(5, 7) === mm ? a.plannedDateTo.slice(8, 10) : dayjs(a.plannedDateTo).format("DD/MM")}` : ""}</b> {a.supplierName}
                  <div style={{ fontSize: 12, color: palette.muted }}>
                    {a.code} · {t(`t_${a.type}`)} · {initials(a.auditorId)}
                  </div>
                </button>
              ))}
            </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
